import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeSubtaskConcludeMode,
  ChangeSubtaskFormSchemaResponse,
  ChangeSubtaskGateStatus,
} from '../../../types/changes';
import { useModal } from '../../../context/ModalContext';
import { buildFriendlyApiError } from '../../../utils/apiError';
import ChangeFormFieldBuilder from './ChangeFormFieldBuilder';
import {
  DEFAULT_PACKAGE_CREATE_STATUSES,
  PACKAGE_CREATE_STATUS_OPTIONS,
} from '../subtaskPackageCreateStatuses';
import { SUBTASK_GATE_STATUS_OPTIONS } from '../subtaskGateStatuses';

const MODE_OPTIONS: Array<{ value: ChangeSubtaskConcludeMode; label: string; hint: string }> = [
  {
    value: 'assignee',
    label: 'Quem recebeu a subtarefa (padrão)',
    hint: 'Só a pessoa designada ou quem assumiu no grupo conclui.',
  },
  {
    value: 'owner',
    label: 'Somente o dono da mudança',
    hint: 'O destinatário trabalha; o dono é quem marca como concluída.',
  },
  {
    value: 'both',
    label: 'Os dois (quem recebeu e o dono)',
    hint: 'Cada um conclui a sua parte. O sistema avisa quem já concluiu e quem falta.',
  },
];

const ChangeSubtaskFormBuilder: React.FC = () => {
  const { showAlert } = useModal();
  const [concludeMode, setConcludeMode] = useState<ChangeSubtaskConcludeMode>('assignee');
  const [buttonLabel, setButtonLabel] = useState('Concluir');
  const [allowCreateAttachments, setAllowCreateAttachments] = useState(false);
  const [internalGate, setInternalGate] = useState<ChangeSubtaskGateStatus>('off');
  const [externalGate, setExternalGate] = useState<ChangeSubtaskGateStatus>('off');
  const [packageCreateStatuses, setPackageCreateStatuses] = useState<string[]>(
    DEFAULT_PACKAGE_CREATE_STATUSES,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = useCallback(async (): Promise<ChangeSubtaskFormSchemaResponse> => {
    const data = await changesApi.getSubtaskFormSchema();
    setConcludeMode(data.conclude_mode || 'assignee');
    setButtonLabel(data.conclude_button_label || 'Concluir');
    setAllowCreateAttachments(Boolean(data.allow_create_attachments));
    setInternalGate(
      data.internal_subtask_gate_before_status ||
        data.checklist_required_before_status ||
        'off',
    );
    setExternalGate(data.external_subtask_gate_before_status || 'off');
    setPackageCreateStatuses(
      data.package_create_statuses?.length
        ? data.package_create_statuses
        : DEFAULT_PACKAGE_CREATE_STATUSES,
    );
    return data;
  }, []);

  useEffect(() => {
    void loadSettings().catch((err: unknown) => {
      const friendly = buildFriendlyApiError(err, {
        defaultMessage: 'Não foi possível carregar a política de conclusão.',
        endpointHints: ['subtask-form'],
      });
      setError(friendly.details ? `${friendly.message} ${friendly.details}` : friendly.message);
    });
  }, [loadSettings]);

  const handleSaveSettings = async () => {
    const label = buttonLabel.trim();
    if (!label) {
      setError('Informe o texto do botão de concluir.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = await changesApi.updateSubtaskFormSettings({
        conclude_mode: concludeMode,
        conclude_button_label: label,
        allow_create_attachments: allowCreateAttachments,
        internal_subtask_gate_before_status: internalGate,
        external_subtask_gate_before_status: externalGate,
        package_create_statuses: packageCreateStatuses,
      });
      setConcludeMode(data.conclude_mode || concludeMode);
      setButtonLabel(data.conclude_button_label || label);
      setAllowCreateAttachments(Boolean(data.allow_create_attachments));
      setInternalGate(data.internal_subtask_gate_before_status || internalGate);
      setExternalGate(data.external_subtask_gate_before_status || externalGate);
      showAlert({
        title: 'Política de subtarefa',
        message:
          'Política salva com sucesso. Vale para novas subtarefas e para quem usa o padrão do cadastro.',
        variant: 'success',
      });
    } catch (err: unknown) {
      const friendly = buildFriendlyApiError(err, {
        defaultMessage: 'Não foi possível salvar a política de conclusão.',
        endpointHints: ['subtask-form'],
      });
      setError(friendly.details ? `${friendly.message} ${friendly.details}` : friendly.message);
    } finally {
      setSaving(false);
    }
  };

  const api = useMemo(
    () => ({
      load: changesApi.getSubtaskFormSchema,
      createField: changesApi.createSubtaskFormField,
      updateField: changesApi.updateSubtaskFormField,
      deleteField: changesApi.deleteSubtaskFormField,
      reorderFields: changesApi.reorderSubtaskFormFields,
    }),
    [],
  );

  return (
    <div className="d-flex flex-column gap-3">
      <Card>
        <Card.Body>
          <h5 className="mb-1">Quem conclui a subtarefa</h5>
          <p className="text-muted">
            O botão de concluir respeita esta regra. O servidor valida o papel; o cliente não
            escolhe se é dono ou destinatário.
          </p>
          {error ? (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}
          <Row className="g-3">
            {MODE_OPTIONS.map((option) => (
              <Col xs={12} key={option.value}>
                <Form.Check
                  type="radio"
                  id={`conclude-mode-${option.value}`}
                  name="conclude-mode"
                  label={option.label}
                  checked={concludeMode === option.value}
                  onChange={() => setConcludeMode(option.value)}
                />
                <Form.Text className="text-muted d-block ms-4">{option.hint}</Form.Text>
              </Col>
            ))}
            <Col xs={12} md={6}>
              <Form.Label htmlFor="conclude-button-label">Texto do botão Concluir</Form.Label>
              <Form.Control
                id="conclude-button-label"
                value={buttonLabel}
                maxLength={80}
                onChange={(e) => setButtonLabel(e.target.value)}
              />
            </Col>
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id="allow-create-attachments"
                label="Permitir anexo na criação da subtarefa"
                checked={allowCreateAttachments}
                onChange={(e) => setAllowCreateAttachments(e.target.checked)}
              />
              <Form.Text className="text-muted d-block">
                Se ligado, quem cria a subtarefa pode enviar arquivo no formulário. Depois da
                criação, o criador também anexa sem precisar assumir a conversa.
              </Form.Text>
            </Col>
            <Col xs={12}>
              <Form.Label className="d-block">Botão «Adicionar subtarefas» (pacote na aba Subtarefas)</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {PACKAGE_CREATE_STATUS_OPTIONS.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`package-status-${option.value}`}
                    label={option.label}
                    checked={packageCreateStatuses.includes(option.value)}
                    onChange={(e) => {
                      setPackageCreateStatuses((prev) => {
                        if (e.target.checked) {
                          return prev.includes(option.value)
                            ? prev
                            : [...prev, option.value];
                        }
                        const next = prev.filter((item) => item !== option.value);
                        return next.length ? next : prev;
                      });
                    }}
                  />
                ))}
              </div>
              <Form.Text className="text-muted d-block">
                Define em quais status da mudança o pacote manual (internas/externas) pode ser
                incluído. O disparo automático na criação continua obedecendo ao tipo de mudança.
              </Form.Text>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label htmlFor="internal-subtask-gate">
                Trava de subtarefas internas (checklist)
              </Form.Label>
              <Form.Select
                id="internal-subtask-gate"
                value={internalGate}
                onChange={(e) => setInternalGate(e.target.value as ChangeSubtaskGateStatus)}
              >
                {SUBTASK_GATE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted d-block">
                Ao tentar avançar para um status depois do escolhido, todas as internas ativas
                precisam estar concluídas. Ex.: em Agendado, com trava em Agendado, só vai para
                Autorizar quando não houver interna em aberto.
              </Form.Text>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label htmlFor="external-subtask-gate">
                Trava de subtarefas externas (Incidente)
              </Form.Label>
              <Form.Select
                id="external-subtask-gate"
                value={externalGate}
                onChange={(e) => setExternalGate(e.target.value as ChangeSubtaskGateStatus)}
              >
                {SUBTASK_GATE_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted d-block">
                Mesma lógica para chamados no Incidente vinculados à mudança. Independente desta
                configuração, sair de Implementar para Revisar sempre exige todas as internas e
                externas concluídas.
              </Form.Text>
            </Col>
          </Row>
          <Button className="mt-3" disabled={saving} onClick={() => void handleSaveSettings()}>
            {saving ? 'Salvando...' : 'Salvar política'}
          </Button>
        </Card.Body>
      </Card>
      <ChangeFormFieldBuilder
        heading="Campos da nova subtarefa"
        description="Segure o ícone ⋮⋮ e arraste para mudar a ordem. Título e destino não somem. Tipo da tarefa lê o cadastro Tipos de tarefa: dá para ocultar ou tornar opcional aqui. A data pode ficar oculta. Campos extras entram na criação e aparecem no histórico."
        api={api}
      />
    </div>
  );
};

export default ChangeSubtaskFormBuilder;
